import type {
  UploadMediaCommandInput,
  UploadMediaCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import type {
  CreateQuestionRequest,
  CreateQuestionResponse,
  SubQuestion,
} from 'src/types/helios/Question';
import type { ReviewQuestion } from './reviewTypes';
import type { CriteriaAttachments } from '../components/AuditCriteriaStep';
import { assembleQuestionMedia, assembleSubQuestionMedia } from './assembleQuestionMedia';
import { mapReviewQuestionToCreateRequest } from './mapReviewQuestionToCreateRequest';

/** Everything the orchestration needs that it can't derive from the draft:
 *  identity, the upload mutation, and the create call. Injected so this stays a
 *  plain (hook-free) async function — testable, and keeping `await` out of the
 *  feature `hooks/` layer. */
export interface PublishReviewQuestionDeps {
  readonly programId: string;
  readonly createdBy: string;
  readonly businessUnit: string;
  readonly uploadMedia: (
    input: UploadMediaCommandInput,
  ) => Promise<UploadMediaCommandOutput>;
  readonly createQuestion: (
    request: CreateQuestionRequest,
  ) => Promise<CreateQuestionResponse>;
}

/**
 * Publishes one reviewed Astra draft end to end: uploads its media (inline
 * images + discrete attachments), rewrites content to media-id srcs, maps to a
 * {@link CreateQuestionRequest}, and creates the question. Resolves with the
 * created question's id; rejects (propagating the upload/create error) so the
 * caller can keep the draft unpublished and surface the message.
 *
 * Plain async orchestration (no React) — the page owns the hooks and passes
 * their callables in via {@link PublishReviewQuestionDeps}.
 */
export async function publishReviewQuestion(
  question: ReviewQuestion,
  attachments: CriteriaAttachments,
  deps: PublishReviewQuestionDeps,
): Promise<CreateQuestionResponse> {
  const { programId, createdBy, businessUnit, uploadMedia, createQuestion } = deps;
  const context = { programId, businessUnit, createdBy };

  // 1. Upload + rewrite the question-level media (criteria fields + task desc).
  const { content, mediaIdsMap } = await assembleQuestionMedia(
    question.content,
    attachments,
    context,
    uploadMedia,
  );

  // 2. Upload + rewrite each sub-question's media, in parallel.
  const subQuestions: SubQuestion[] = await Promise.all(
    (question.content.subQuestions ?? []).map((sub) =>
      assembleSubQuestionMedia(sub, context, uploadMedia),
    ),
  );

  // 3. Map to the create request and publish.
  const request = mapReviewQuestionToCreateRequest(question, {
    programId,
    createdBy,
    content,
    mediaIdsMap,
    subQuestions,
  });
  return createQuestion(request);
}
